import { useState, useEffect } from 'react';
import { apiService } from '../services/api';

export interface PaymentStatus {
  loading: boolean;
  success: boolean;
  error: string | null;
  paymentData: any | null;
  pollingStatus?: string; // Add polling status for better UX
}

export const usePaymentStatus = (sessionId: string | undefined): PaymentStatus => {
  const [status, setStatus] = useState<PaymentStatus>({
    loading: true,
    success: false,
    error: null,
    paymentData: null,
    pollingStatus: undefined
  });

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus({
          loading: false,
          success: false,
          error: 'Invalid session ID',
          paymentData: null,
          pollingStatus: undefined
        });
        return;
      }

      try {
        // Check if it's a Stripe session ID
        if (sessionId.startsWith('cs_')) {
          setStatus(prev => ({ ...prev, pollingStatus: 'Checking payment status...' }));
          
          // Use the enhanced polling method with timeout handling
          const pollResult = await apiService.pollPaymentStatus(sessionId, 30, 1000);
          
          if (pollResult.status === 'confirmed') {
            setStatus({
              loading: false,
              success: true,
              error: null,
              paymentData: {
                success: true,
                payment: pollResult.payment
              },
              pollingStatus: 'Payment confirmed!'
            });
          } else if (pollResult.status === 'failed') {
            setStatus({
              loading: false,
              success: false,
              error: 'Payment failed',
              paymentData: null,
              pollingStatus: 'Payment failed'
            });
          } else if (pollResult.status === 'cancelled') {
            setStatus({
              loading: false,
              success: false,
              error: 'Payment was cancelled',
              paymentData: null,
              pollingStatus: 'Payment cancelled'
            });
          } else {
            // Payment still pending after timeout
            setStatus({
              loading: false,
              success: false,
              error: 'Payment confirmation timeout. Please check your email or contact support.',
              paymentData: pollResult.payment ? { success: true, payment: pollResult.payment } : null,
              pollingStatus: 'Payment status unclear'
            });
          }
        } else {
          // For non-Stripe sessions (free tickets, etc.)
          setStatus({
            loading: false,
            success: true,
            error: null,
            paymentData: {
              success: true,
              payment: {
                id: sessionId,
                amount: 0,
                currency: 'EUR',
                status: 'confirmed',
                metadata: {
                  eventTitle: 'Free Event',
                  userName: 'Free Ticket Holder'
                }
              }
            },
            pollingStatus: 'Free ticket confirmed'
          });
        }
      } catch (error: any) {
        console.error('Error checking payment status:', error);
        
        // If polling timeout, provide helpful message
        if (error.message?.includes('timeout')) {
          setStatus({
            loading: false,
            success: false,
            error: 'Payment verification is taking longer than expected. Please check your email for confirmation or contact support.',
            paymentData: null,
            pollingStatus: 'Verification timeout'
          });
        } else {
          setStatus({
            loading: false,
            success: false,
            error: error.message || 'Failed to check payment status',
            paymentData: null,
            pollingStatus: 'Status check failed'
          });
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  return status;
};